import { useEffect, useMemo, useState } from "react";
import { Feature } from "geojson";
import { isEqual } from "lodash";

import { layersSlice, selectLayerByVersionAndConfigId, selectLayerConfigByKey } from "fond/api";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";

interface LayerFeaturesResult {
  layerKey: string;
  features: Feature[];
  isError: boolean;
}

/**
 * Fetch features for multiple layers, returned grouped by layer key.
 *
 * For each layer key the hook looks up the layer's ID for the given version and (if the layer
 * is uploaded) fetches its features via RTK Query. Returned features are deep-cloned so callers
 * can mutate freely without affecting the cache. Layers without uploaded data appear in the
 * result map with an empty array.
 *
 * @param versionId - The version ID to fetch layers from
 * @param layerKeys - Array of layer keys
 *
 * @example
 * const { featuresByLayer, isLoading } = useFeaturesByLayer(versionId, ["demand", "leadins"]);
 * // featuresByLayer.demand, featuresByLayer.leadins
 */
export const useFeaturesByLayer = (
  versionId: string,
  layerKeys: string[]
): {
  featuresByLayer: Record<string, Feature[]>;
  isLoading: boolean;
  isError: boolean;
} => {
  const dispatch = useAppDispatch();
  const [layerResults, setLayerResults] = useState<LayerFeaturesResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Get all layer metadata in one selector call with deep equality check
  // Use LayerConfig first (always available), then get Layer if it exists (only when features uploaded)
  const layers = useAppSelector((state) => {
    return layerKeys.map((layerKey) => {
      const layerConfig = selectLayerConfigByKey(state, { versionId, layerKey });
      const layer = layerConfig ? selectLayerByVersionAndConfigId(state, { versionId, configId: layerConfig.ID }) : undefined;
      return {
        layerKey,
        layerId: layer?.ID, // undefined if layer not uploaded = no features to fetch
      };
    });
  }, isEqual);

  // Fetch features for all layers using dispatch
  useEffect(() => {
    // Skip if no layers
    if (layerKeys.length === 0) {
      setLayerResults([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    // Track subscriptions for cleanup
    const subscriptions: Array<{ unsubscribe: () => void }> = [];

    // Create promises for all layer fetches
    const fetchPromises = layers.map(async ({ layerKey, layerId }) => {
      if (!layerId) {
        return { layerKey, features: [], isError: false };
      }

      // Use RTK Query's initiate method to fetch data
      const querySubscription = dispatch(layersSlice.endpoints.getFeatures.initiate({ layerId }, { forceRefetch: false }));
      subscriptions.push(querySubscription);

      try {
        const result = await querySubscription.unwrap();
        // Clone so callers can mutate without affecting the cache
        return {
          layerKey,
          features: result ? structuredClone(result) : [],
          isError: false,
        };
      } catch (error) {
        console.error(`Failed to fetch features for layer ${layerKey}:`, error);
        return { layerKey, features: [], isError: true };
      } finally {
        // Unsubscribe after data is received
        querySubscription.unsubscribe();
      }
    });

    // Wait for all fetches to complete
    Promise.all(fetchPromises).then((results) => {
      if (!cancelled) {
        setLayerResults(results);
        setIsLoading(false);
      }
    });

    // Cleanup: unsubscribe from any remaining subscriptions on unmount or re-run
    return () => {
      cancelled = true;
      subscriptions.forEach((sub) => sub.unsubscribe());
    };
  }, [dispatch, layers, layerKeys.length]);

  // Group results by layer key
  return useMemo(() => {
    const featuresByLayer: Record<string, Feature[]> = {};
    let isError = false;
    layerResults.forEach((result) => {
      featuresByLayer[result.layerKey] = result.features;
      if (result.isError) {
        isError = true;
      }
    });
    return { featuresByLayer, isLoading, isError };
  }, [layerResults, isLoading]);
};
